import React, { useState } from 'react';
import { Check, X, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../../lib/api.js';
import { fmtFromNow, initials } from '../../lib/format.js';

/**
 * "Pending Account Sync Requests" safety-gate card shown to managers/admins on
 * Dashboard and Team. Renders nothing when the list is empty.
 * @param {Object} props
 * @param {Object[]} [props.items] - Pending SyncRequest docs from GET /api/sync/pending.
 * @param {() => void} [props.onApproved] - Called after an approve/decline so callers refetch.
 * @returns {JSX.Element|null}
 */
export default function PendingSyncRequestsCard({ items = [], onApproved }) {
  const [busyId, setBusyId] = useState(null);

  if (items.length === 0) return null;

  async function handleApprove(requestId, agentName) {
    setBusyId(requestId);
    try {
      const res = await apiPost('/sync/approve', { requestId });
      if (res?.ok) {
        toast.success(`Settings successfully synced to ${agentName || 'Agent'}!`);
        onApproved?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Sync approval failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(requestId) {
    if (!confirm('Decline this sync request?')) return;
    setBusyId(requestId);
    try {
      const res = await apiPost('/sync/reject', { requestId });
      if (res?.ok) {
        toast('Sync request declined');
        onApproved?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Decline failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="panel p-0 overflow-hidden border border-civix-purple/40 bg-civix-purple/5 shadow-xl rounded-xl">
      <div className="px-5 py-3.5 bg-civix-purple/15 border-b border-civix-purple/20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <RefreshCw className="w-5 h-5 text-civix-cyan animate-spin-slow" />
          <h3 className="font-display font-bold text-white text-base">
            Pending Account Sync Requests
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-civix-purple text-white">
            {items.length}
          </span>
        </div>
        <span className="text-xs text-civix-purpleSoft/90 hidden sm:inline">
          Agents requesting to copy your AI bots, SMS drips, email sequences & tags
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/50 text-xs border-b border-white/5 bg-black/20">
            <tr>
              <th className="py-2.5 px-4 font-medium">Agent</th>
              <th className="py-2.5 px-4 font-medium">Assets Requested</th>
              <th className="py-2.5 px-4 font-medium">Requested</th>
              <th className="py-2.5 px-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((r) => {
              const requester = r.requesterId || r.fromAgent;
              const name = requester?.name || requester?.email || 'Downline Agent';
              const email = requester?.email || '';
              const isBusy = busyId === r._id;

              return (
                <tr key={r._id} className="hover:bg-white/5 transition">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-civix-gradient flex items-center justify-center text-xs font-bold shrink-0">
                        {requester?.avatar
                          ? <img src={requester.avatar} alt={name} className="w-full h-full object-cover" />
                          : initials(name)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-white">{name}</div>
                        <div className="text-xs text-white/40">{email}</div>
                        {r.note && (
                          <div className="text-xs text-white/60 italic mt-0.5 max-w-md truncate">
                            "{r.note}"
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[10px] px-2 py-0.5 rounded bg-civix-purple/20 text-civix-purpleSoft border border-civix-purple/30">
                        AI Text Bots
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-civix-cyan/20 text-civix-cyan border border-civix-cyan/30">
                        SMS Drips
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        Email Templates
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        Tags & Pipeline
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs text-white/50">
                    {fmtFromNow(r.requestedAt || r.createdAt)}
                  </td>
                  <td className="py-3 px-4 text-right space-x-2">
                    <button
                      onClick={() => handleApprove(r._id, name)}
                      disabled={isBusy}
                      className="px-3.5 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold shadow-sm transition inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" />
                      {isBusy ? 'Syncing…' : 'Approve Sync'}
                    </button>
                    <button
                      onClick={() => handleReject(r._id)}
                      disabled={isBusy}
                      className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-rose-500/20 text-white/70 hover:text-rose-300 text-xs font-medium border border-white/10 transition inline-flex items-center gap-1 cursor-pointer disabled:opacity-50"
                    >
                      <X className="w-3.5 h-3.5" /> Decline Sync
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
